import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import { useEffect } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Fonts, Palette, Spacing } from '@/constants/theme';
import {
  HOME_CAP,
  getSubscriptionTier,
  listProperties,
} from '@/lib/tasks';
import { useOnboarding } from '@/store/onboarding';

/**
 * /add-home — entry point into the SECOND home (and beyond) setup.
 *
 * Originally Phase 3 was a bespoke 3-step quick wizard at this route,
 * but the spec was reconsidered: the user wanted parity with the
 * first-home flow — same Quick / Full choice, same screens, same
 * mental model. Reusing the existing `(onboarding)` group is also
 * less code to maintain and means every onboarding improvement
 * (new lifestyle flags, climate-aware defaults, etc.) automatically
 * lands for add-home too.
 *
 * What this screen does:
 *   1. Tier-gate (free → 1 home; paid → 5).
 *   2. Reset the zustand onboarding store, preserving owner name from
 *      the first home so the user doesn't retype their identity.
 *   3. Redirect to /(onboarding)/welcome — from there the user picks
 *      Quick or Full on the path screen and runs the same flow.
 *   4. Commit handling (plan.tsx) is multi-home-aware: it calls
 *      setActiveProperty after the new property is created so the
 *      user lands on My Home looking at the home they just added.
 *
 * Shown as a brief loading flash because the redirect happens in a
 * single useEffect — never a meaningful render.
 */
export default function AddHome() {
  const router = useRouter();
  const db = useSQLiteContext();
  const resetForNewHome = useOnboarding((s) => s.resetForNewHome);

  useEffect(() => {
    void (async () => {
      // Tier gate. Even though the HomeSwitcher already disables the
      // "+ Add a home" button at the cap, anyone deep-linking straight
      // to /add-home should hit the same wall. Bounce back to My Home
      // with no fanfare — the switcher's chip explained the cap.
      const [tier, props] = await Promise.all([
        getSubscriptionTier(db),
        listProperties(db),
      ]);
      const cap = HOME_CAP[tier];
      if (props.length >= cap) {
        router.replace('/(tabs)/my-home' as never);
        return;
      }

      // Pre-fill owner name from whatever first-onboarded property has
      // it. Owner identity is account-level, not per-home — the user
      // shouldn't retype their name for every property they own. If
      // no first home has an owner name set (older properties), we
      // pass empty strings and the welcome screen captures them like
      // a fresh install would.
      const firstWithOwner = props.find(
        (p) => p.owner_first_name?.trim() || p.owner_last_name?.trim()
      );
      resetForNewHome(
        firstWithOwner?.owner_first_name ?? '',
        firstWithOwner?.owner_last_name ?? ''
      );

      // Forward into the existing onboarding flow. `replace` (not push)
      // so the back button from the welcome screen goes to wherever
      // they came from, not back to this empty redirect.
      router.replace('/(onboarding)/welcome' as never);
    })();
  }, [db, resetForNewHome, router]);

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.body}>
        <ActivityIndicator color={Palette.forest} />
        <Text style={styles.label}>Starting setup…</Text>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  body: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.three,
  },
  label: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.inkSoft,
    letterSpacing: 0.4,
  },
});
